import { cva } from 'class-variance-authority'
import type { HTMLAttributes } from 'react'
import { cn } from '../lib/cn.ts'

// Operational variants (ok, queued, waiting, fail, attention) are the status
// vocabulary of the command center; the legacy names map onto the same tints so
// existing pages stay coherent under the dark theme. Every badge renders its
// text token: color is never the only carrier of meaning.
export type BadgeVariant =
  | 'neutral'
  | 'info'
  | 'success'
  | 'warning'
  | 'danger'
  | 'ok'
  | 'queued'
  | 'waiting'
  | 'fail'
  | 'attention'

export const badgeVariants = cva(
  'inline-flex items-center gap-1 rounded-none border px-1.5 py-[3px] font-mono text-[9px] font-medium uppercase leading-none tracking-[0.08em]',
  {
    variants: {
      variant: {
        neutral: 'border-border-hairline bg-ground-canvas text-ink-muted',
        info: 'border-signal-queued-border bg-signal-queued-bg text-signal-queued',
        success: 'border-signal-ok-border bg-signal-ok-bg text-signal-ok',
        warning: 'border-signal-waiting-border bg-signal-waiting-bg text-signal-waiting',
        danger: 'border-signal-fail-border bg-signal-fail-bg text-signal-fail',
        ok: 'border-signal-ok-border bg-signal-ok-bg text-signal-ok',
        queued: 'border-signal-queued-border bg-signal-queued-bg text-signal-queued',
        waiting: 'border-signal-waiting-border bg-signal-waiting-bg text-signal-waiting',
        fail: 'border-signal-fail-border bg-signal-fail-bg text-signal-fail',
        attention: 'border-signal-oxide bg-signal-oxide text-ink-primary'
      }
    },
    defaultVariants: {
      variant: 'neutral'
    }
  }
)

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant
}

export function Badge({ variant = 'neutral', className, ...rest }: BadgeProps) {
  return <span className={cn(badgeVariants({ variant }), className)} {...rest} />
}
